import React, { useEffect, useRef } from 'react';
import { captcha_key } from './constants';

const showRecaptchaBadge = () => {
  window.__recaptchaVisibleCount = (window.__recaptchaVisibleCount || 0) + 1;
  if (document.body) {
    document.body.classList.add('recaptcha-visible');
  }
};

const hideRecaptchaBadge = () => {
  window.__recaptchaVisibleCount = Math.max(0, (window.__recaptchaVisibleCount || 1) - 1);
  if (window.__recaptchaVisibleCount === 0 && document.body) {
    document.body.classList.remove('recaptcha-visible');
  }
};

/**
 * Working reCAPTCHA v3 (no react-google-recaptcha-v3 package).
 * Badge is shown only while this component is mounted.
 * Props match RecaptchaV3Common: onVerify, refreshReCaptcha
 */
const GoogleRecaptchaV3 = ({ onVerify, refreshReCaptcha, action = 'submit' }) => {
  const onVerifyRef = useRef(onVerify);

  useEffect(() => {
    onVerifyRef.current = onVerify;
  }, [onVerify]);

  // Show badge only on pages that mount this component
  useEffect(() => {
    showRecaptchaBadge();
    return function cleanupBadge() {
      hideRecaptchaBadge();
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadScript = () =>
      new Promise((resolve, reject) => {
        if (window.grecaptcha && typeof window.grecaptcha.execute === 'function') {
          resolve();
          return;
        }

        const existing = document.querySelector('script[src*="recaptcha/api.js"]');
        if (existing) {
          if (window.grecaptcha) {
            resolve();
            return;
          }
          const onLoad = () => resolve();
          const onError = () => reject(new Error('Failed to load reCAPTCHA script'));
          existing.addEventListener('load', onLoad);
          existing.addEventListener('error', onError);
          return;
        }

        const script = document.createElement('script');
        script.src = 'https://www.google.com/recaptcha/api.js?render=' + captcha_key;
        script.async = true;
        script.onload = function () { resolve(); };
        script.onerror = function () { reject(new Error('Failed to load reCAPTCHA script')); };
        document.body.appendChild(script);
      });

    const run = async () => {
      if (!captcha_key) return;

      try {
        await loadScript();

        await new Promise((resolve) => {
          if (window.grecaptcha && typeof window.grecaptcha.ready === 'function') {
            window.grecaptcha.ready(function () { resolve(); });
          } else {
            resolve();
          }
        });

        if (cancelled) return;
        if (!window.grecaptcha || typeof window.grecaptcha.execute !== 'function') return;

        const token = await window.grecaptcha.execute(captcha_key, { action: action });
        if (!cancelled && token && typeof onVerifyRef.current === 'function') {
          onVerifyRef.current(token);
        }
      } catch (err) {
        console.error('reCAPTCHA error:', (err && err.message) || err);
      }
    };

    run();

    return function cleanup() {
      cancelled = true;
    };
  }, [refreshReCaptcha, action]);

  return null;
};

export default GoogleRecaptchaV3;
